import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { LocationsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";

/**
 * Public location detail - optional auth, same pattern as class/teacher GET.
 */
export const useLocationGetPublic = (
  locationId: number,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const isRestoring = useIsRestoring();

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } = useQuery({
    queryKey: [QueryKeys.Locations, locationId, "public"],
    queryFn: () => {
      if (accessToken) {
        const service = createAuthenticatedApi(LocationsApi, accessToken);
        return service.getLocationById({ id: locationId, xTenantID: tenantId });
      }
      const service = new LocationsApi(createOpenApiConfiguration());
      return service.getLocationById({ id: locationId, xTenantID: tenantId });
    },
    enabled: options?.enabled !== false && locationId > 0 && !isRestoring,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
